import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ApplicationFieldConfig, Association } from '../../../../../../models/association';
import { AssociationService } from '../../../../../../services/association/association.service';
import { AccountService } from '../../../../../../services/account/account.service';
import { MyAccount } from '../../../../../../models/my-account';
import { LightspeedCustomerGroup, SquareCustomerGroup } from '../../../../../../models/organization-config';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { EventTypeStatus } from '../../../../../../models/event-type-enums';
import { EventType, EventTypeCategory, getEventTypeStatusDisplay } from '../../../../../../models/event-type';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SelectEventCategoryModalComponent } from '../../../modals/select-event-category-modal/select-event-category-modal.component';
import { SelectEventTypeModalComponent } from '../../../modals/select-event-type-modal/select-event-type-modal.component';
import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import { EntryPassStatus } from '../../../../../../models/entry-pass-enums';
import { EntryPass, getEntryPassStatusDisplay } from '../../../../../../models/entry-pass';
import { SelectEntryPassModalComponent } from '../../../modals/select-entry-pass-modal/select-entry-pass-modal.component';
import { RemoveAssociationModalComponent } from '../modals/remove-association-modal/remove-association-modal.component';
import { dateStringFromISO8601 } from '../../../../../../utils';
import { OrgPOSSoftware } from '../../../../../../models/organization-config-enums';


enum AutomaticRemovalSetting {
  whenManuallyRemoved,
  afterSetInterval,
  onSetDates
}


@Component({
  selector: 'app-configure-association',
  templateUrl: './configure-association.component.html',
  styleUrls: ['./configure-association.component.css']
})
export class ConfigureAssociationComponent implements OnInit, ComponentHasUnsavedChanges {
  public association: Association = null;
  public profileImageFileData: File = null;
  public automaticRemovalSetting: AutomaticRemovalSetting = null;
  public automaticRemovalDateToAdd = '';
  public hasAutoAddOnBookingEvents = false;
  public hasAutoAddOnCheckInEvents = false;
  public hasAutoAddEntryPasses = false;

  public errorMessage = '';
  public errors: any = {};

  public squareCustomerGroupList: SquareCustomerGroup[];
  public lightspeedCustomerGroupList: LightspeedCustomerGroup[];

  public loadingAssociation = true;
  public loadingEventTypes = true;
  public loadingEventCategories = true;
  public loadingSquareCustomerGroups = true;
  public loadingLightspeedCustomerGroups = true;
  public loadingSubmit = false;

  public notFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public myAccount: MyAccount;

  public readonly AutomaticRemovalSetting = AutomaticRemovalSetting;
  public readonly ApplicationFieldConfig = ApplicationFieldConfig;
  public readonly OrgPOSSoftware = OrgPOSSoftware;
  public readonly EventTypeStatus = EventTypeStatus;
  public readonly getEventTypeStatusDisplay = getEventTypeStatusDisplay;
  public readonly EntryPassStatus = EntryPassStatus;
  public readonly getEntryPassStatusDisplay = getEntryPassStatusDisplay;
  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private modalService: NgbModal,
    private associationService: AssociationService,
    private accountService: AccountService,
    public orgConfig: OrganizationConfigService
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    const idInt = parseInt(routeParams.id, 10);
    if (isNaN(idInt)) {
      this.notFound = true;
      this.loadingAssociation = false;
      return;
    }

    this.loadAssociation(idInt);
    this.loadSquareCustomerGroups();
    this.loadLightspeedCustomerGroups();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public loadAssociation(id: number) {
    this.loadingAssociation = true;

    this.associationService
      .getAssociation(id)
      .then(association => {
        this.association = association;

        if (this.association.automatic_removal_interval_months) {
          this.automaticRemovalSetting = AutomaticRemovalSetting.afterSetInterval;
        } else if (this.association.automatic_removal_dates.length > 0) {
          this.automaticRemovalSetting = AutomaticRemovalSetting.onSetDates;
        } else {
          this.automaticRemovalSetting = AutomaticRemovalSetting.whenManuallyRemoved;
        }

        this.hasAutoAddOnBookingEvents = (
          (this.association.auto_add_on_event_booking_types.length > 0) ||
          (this.association.auto_add_on_event_booking_categories.length > 0)
        );
        this.hasAutoAddOnCheckInEvents = (
          (this.association.auto_add_on_event_check_in_types.length > 0) ||
          (this.association.auto_add_on_event_check_in_categories.length > 0)
        );
        this.hasAutoAddEntryPasses = this.association.auto_add_on_entry_pass_use_pass_types.length > 0;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingAssociation = false; });
  }

  public handleSubmit() {
    this.errorMessage = '';
    this.errors = {};

    switch (this.automaticRemovalSetting) {
      case AutomaticRemovalSetting.whenManuallyRemoved:
        this.association.automatic_removal_interval_months = null;
        this.association.automatic_removal_dates = [];
        break;
      case AutomaticRemovalSetting.afterSetInterval:
        this.association.automatic_removal_dates = [];
        if (!this.association.automatic_removal_interval_months) {
          this.errors.automatic_removal_interval_months = 'This field is required.';
          this.errorMessage = 'Please correct the errors shown and try again.';
          return;
        }
        break;
      case AutomaticRemovalSetting.onSetDates:
        this.association.automatic_removal_interval_months = null;
        if (this.association.automatic_removal_dates.length === 0) {
          this.errors.automatic_removal_dates = 'At least one date must be provided.';
          this.errorMessage = 'Please correct the errors shown and try again.';
          return;
        }
    }

    if (!this.hasAutoAddOnBookingEvents) {
      this.association.auto_add_on_event_booking_types = [];
      this.association.auto_add_on_event_booking_categories = [];
    }
    if (!this.hasAutoAddOnCheckInEvents) {
      this.association.auto_add_on_event_check_in_types = [];
      this.association.auto_add_on_event_check_in_categories = [];
    }
    if (!this.hasAutoAddEntryPasses) {
      this.association.auto_add_on_entry_pass_use_pass_types = [];
    }

    this.loadingSubmit = true;

    this.associationService
      .updateAssociation(this.association, this.profileImageFileData)
      .then(savedAssociation => {
        this.unsavedChanges = false;

        this.association = savedAssociation;

        const successMessage = this.association.name + ' updated successfully.';
        this.router.navigate(
          ['/associations', this.association.id.toString()],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to configure this association.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  /**
   * The event handler for the profile image file input.
   * - Ensures that the file is an image.
   * - Saves the file data in profileImageFileData.
   * - Sets the file location as the association's profile image (for preview display).
   */
  public attachProfileImageFile(event) {
    this.profileImageFileData = event.target.files[0] as File;
    if (this.profileImageFileData.type.match(/image\/*/) == null) {
      this.profileImageFileData = null;
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(this.profileImageFileData);
    reader.onload = () => {
      this.association.profile_image = reader.result as string;
    };
    this.unsavedChanges = true;
  }

  public openAddAutoAddEventBookingTypeModal() {
    const modalRef = this.modalService.open(SelectEventTypeModalComponent);
    modalRef.componentInstance.excludeIds = this.association
      .auto_add_on_event_booking_types.map(et => et.id);
    modalRef.componentInstance.filters = {status: EventTypeStatus.active};
    modalRef.componentInstance.submitCallback = (result: EventType) => {
      this.association.auto_add_on_event_booking_types.push({
        id: result.id,
        name: result.name,
        status: result.status
      });
      this.unsavedChanges = true;
    }
  }

  public openAddAutoAddEventBookingCategoryModal() {
    const modalRef = this.modalService.open(SelectEventCategoryModalComponent);
    modalRef.componentInstance.excludeIds = this.association
      .auto_add_on_event_booking_categories.map(cat => cat.id);
    modalRef.componentInstance.submitCallback = (result: EventTypeCategory) => {
      this.association.auto_add_on_event_booking_categories.push({
        id: result.id,
        name: result.name
      });
      this.unsavedChanges = true;
    }
  }

  public openAddAutoAddEventCheckInTypeModal() {
    const modalRef = this.modalService.open(SelectEventTypeModalComponent);
    modalRef.componentInstance.excludeIds = this.association
      .auto_add_on_event_check_in_types.map(et => et.id);
    modalRef.componentInstance.filters = {status: EventTypeStatus.active};
    modalRef.componentInstance.submitCallback = (result: EventType) => {
      this.association.auto_add_on_event_check_in_types.push({
        id: result.id,
        name: result.name,
        status: result.status
      });
      this.unsavedChanges = true;
    }
  }

  public openAddAutoAddEventCheckInCategoryModal() {
    const modalRef = this.modalService.open(SelectEventCategoryModalComponent);
    modalRef.componentInstance.excludeIds = this.association
      .auto_add_on_event_check_in_categories.map(cat => cat.id);
    modalRef.componentInstance.submitCallback = (result: EventTypeCategory) => {
      this.association.auto_add_on_event_check_in_categories.push({
        id: result.id,
        name: result.name
      });
      this.unsavedChanges = true;
    }
  }

  public openAddAutoAddEntryPassModal() {
    const modalRef = this.modalService.open(SelectEntryPassModalComponent);
    modalRef.componentInstance.excludeIds = this.association
      .auto_add_on_entry_pass_use_pass_types.map(ep => ep.id);
    modalRef.componentInstance.filters = {status: EntryPassStatus.active};
    modalRef.componentInstance.submitCallback = (result: EntryPass) => {
      this.association.auto_add_on_entry_pass_use_pass_types.push({
        id: result.id,
        name: result.name,
        status: result.status
      });
      this.unsavedChanges = true;
    }
  }

  public resyncSquareCustomerGroups() {
    if (!this.orgConfig.getOrganizationConfig() || (this.orgConfig.getOrganizationConfig().pos_software !== OrgPOSSoftware.square)) {
      return;
    }

    this.loadingSquareCustomerGroups = true;

    this.orgConfig
      .syncSquareCustomerGroups()
      .then(() => {
        return this.loadSquareCustomerGroups();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingSquareCustomerGroups = false; });
  }

  public resyncLightspeedCustomerGroups() {
    if (!this.orgConfig.getOrganizationConfig() || (this.orgConfig.getOrganizationConfig().pos_software !== OrgPOSSoftware.lightspeed)) {
      return;
    }

    this.loadingLightspeedCustomerGroups = true;

    this.orgConfig
      .syncLightspeedCustomerGroups()
      .then(() => {
        return this.loadLightspeedCustomerGroups();
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingLightspeedCustomerGroups = false; });
  }

  public openRemoveAssociationModal() {
    const modalRef = this.modalService.open(RemoveAssociationModalComponent);
    modalRef.componentInstance.association = this.association;
  }

  private loadSquareCustomerGroups(): Promise<void> {
    this.loadingSquareCustomerGroups = true;

    if (this.orgConfig.getOrganizationConfig() && (this.orgConfig.getOrganizationConfig().pos_software === OrgPOSSoftware.square)) {
      return this.orgConfig
        .getSquareCustomerGroups()
        .then(response => {
          this.squareCustomerGroupList = response.filter(cg => cg.is_managed_by_capitan);
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.accountService.logout();
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingSquareCustomerGroups = false; });
    } else {
      this.squareCustomerGroupList = [];
      this.loadingSquareCustomerGroups = false;
      return Promise.resolve();
    }
  }

  private loadLightspeedCustomerGroups(): Promise<void> {
    this.loadingLightspeedCustomerGroups = true;

    if (this.orgConfig.getOrganizationConfig() && (this.orgConfig.getOrganizationConfig().pos_software === OrgPOSSoftware.lightspeed)) {
      return this.orgConfig
        .getLightspeedCustomerGroups()
        .then(response => {
          this.lightspeedCustomerGroupList = response
            .filter(cg => cg.is_managed_by_capitan)
            .sort((a, b) => a.name < b.name ? -1 : 1);
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.accountService.logout();
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingLightspeedCustomerGroups = false; });
    } else {
      this.lightspeedCustomerGroupList = [];
      this.loadingLightspeedCustomerGroups = false;
      return Promise.resolve();
    }
  }
}
